/**
 * creature/pose.ts — The Language of Bodies
 *
 * A pose is how a creature holds itself: which joints are bent, how the
 * tail wags, whether the eyes are open. It's a sparse map of overrides
 * on top of the skeleton's rest angles, plus global modifiers for things
 * that cross-cut multiple joints (breathing, blinking, tail wagging).
 *
 * Poses are interpolated — the creature is always blending from its
 * current pose toward a target pose. This is what makes movement feel
 * alive rather than switched. The lerp rate (3.5 * dt) comes from the
 * original title.ts cat and gives a ~0.3s blend time that feels natural.
 */

// ─── Creature Pose ──────────────────────────────────────────────────────────

export interface CreaturePose {
  /**
   * Per-joint angle overrides (radians, relative to rest angle).
   * Only joints that differ from rest need entries.
   */
  joints: Record<string, number>;

  // ── Global modifiers (cross-cut multiple joints) ──────────────
  /** 0 = jaw closed, 1 = fully open (yawning) */
  jawOpen: number;
  /** 0 = eyes closed (sleeping), 1 = fully open */
  eyeOpen: number;
  /** Left ear deflection: -1..1 */
  earTwitchL: number;
  /** Right ear deflection: -1..1 */
  earTwitchR: number;
  /** Tail wag oscillation amplitude (0 = still, 1 = vigorous) */
  tailWag: number;
  /** Tail curl amount (0 = straight, 1 = tightly curled) */
  tailCurl: number;
  /** Walk cycle phase (radians, 0..2PI) */
  walkPhase: number;
  /** Breathing cycle phase (radians) */
  breathePhase: number;
  /** World-space offset from home position */
  walkOffset: { x: number; y: number };

  // ── Spine / posture ───────────────────────────────────────────
  /** 0 = flat (lying down), 0.5 = angled, 1 = fully upright */
  spineAngle: number;
  /** Head lift: 0 = resting, 1 = fully raised */
  headLift: number;
  /** Head turn: -1 = left, 0 = center, 1 = right */
  headTurn: number;

  // ── Special actions ───────────────────────────────────────────
  /** Scratch intensity: 0 = none, 1 = full scratch motion */
  scratchLeg: number;
}

// ─── Factory ────────────────────────────────────────────────────────────────

/** Create a default (rest) pose */
export function zeroPose(): CreaturePose {
  return {
    joints: {},
    jawOpen: 0,
    eyeOpen: 0,
    earTwitchL: 0,
    earTwitchR: 0,
    tailWag: 0,
    tailCurl: 0.3,
    walkPhase: 0,
    breathePhase: 0,
    walkOffset: { x: 0, y: 0 },
    spineAngle: 0,
    headLift: 0,
    headTurn: 0,
    scratchLeg: 0,
  };
}

/** Clone a pose (deep copy) */
export function clonePose(pose: CreaturePose): CreaturePose {
  return {
    joints: { ...pose.joints },
    jawOpen: pose.jawOpen,
    eyeOpen: pose.eyeOpen,
    earTwitchL: pose.earTwitchL,
    earTwitchR: pose.earTwitchR,
    tailWag: pose.tailWag,
    tailCurl: pose.tailCurl,
    walkPhase: pose.walkPhase,
    breathePhase: pose.breathePhase,
    walkOffset: { x: pose.walkOffset.x, y: pose.walkOffset.y },
    spineAngle: pose.spineAngle,
    headLift: pose.headLift,
    headTurn: pose.headTurn,
    scratchLeg: pose.scratchLeg,
  };
}

// ─── Interpolation ──────────────────────────────────────────────────────────

/**
 * Lerp current pose toward target at the given rate.
 * Mutates `current` in place.
 *
 * Rate semantics: rate = 3.5 * dt gives ~0.3s blend (the title.ts cat rate).
 * Some properties blend faster (jaw, eyes at 2x; scratch at 4x).
 */
export function lerpPose(
  current: CreaturePose,
  target: CreaturePose,
  rate: number
): void {
  const r = Math.min(rate, 1); // clamp to avoid overshoot
  const rFast = Math.min(rate * 2, 1); // fast channels (jaw, eyes)
  const rSnap = Math.min(rate * 4, 1); // snap channels (scratch)

  // Spine / posture
  current.spineAngle += (target.spineAngle - current.spineAngle) * r;
  current.headLift += (target.headLift - current.headLift) * r;
  current.headTurn += (target.headTurn - current.headTurn) * r;

  // Fast-responding
  current.jawOpen += (target.jawOpen - current.jawOpen) * rFast;
  current.eyeOpen += (target.eyeOpen - current.eyeOpen) * rFast;

  // Tail
  current.tailCurl += (target.tailCurl - current.tailCurl) * r;
  current.tailWag += (target.tailWag - current.tailWag) * r;

  // Snap
  current.scratchLeg += (target.scratchLeg - current.scratchLeg) * rSnap;

  // Ear twitches are usually set procedurally, but lerp target if set
  current.earTwitchL += (target.earTwitchL - current.earTwitchL) * r;
  current.earTwitchR += (target.earTwitchR - current.earTwitchR) * r;

  // Per-joint angles
  const allKeys = new Set([
    ...Object.keys(current.joints),
    ...Object.keys(target.joints),
  ]);
  for (const key of allKeys) {
    const cur = current.joints[key] ?? 0;
    const tgt = target.joints[key] ?? 0;
    const val = cur + (tgt - cur) * r;
    // Remove near-zero entries to keep sparse
    if (Math.abs(val) < 0.0001) {
      delete current.joints[key];
    } else {
      current.joints[key] = val;
    }
  }

  // Walk offset decays if target is zero
  current.walkOffset.x += (target.walkOffset.x - current.walkOffset.x) * r;
  current.walkOffset.y += (target.walkOffset.y - current.walkOffset.y) * r;
}

// ─── Pose Composition ───────────────────────────────────────────────────────

/**
 * Apply a partial pose override onto a target, like the title.ts pattern:
 *   Object.assign(target, ZERO_POSE, stateTarget)
 *
 * Resets target to zero first, then applies the override values.
 */
export function applyPoseOverride(
  target: CreaturePose,
  override: Partial<CreaturePose>
): void {
  // Reset to zero
  const z = zeroPose();
  target.jawOpen = z.jawOpen;
  target.eyeOpen = z.eyeOpen;
  target.earTwitchL = z.earTwitchL;
  target.earTwitchR = z.earTwitchR;
  target.tailWag = z.tailWag;
  target.tailCurl = z.tailCurl;
  target.spineAngle = z.spineAngle;
  target.headLift = z.headLift;
  target.headTurn = z.headTurn;
  target.scratchLeg = z.scratchLeg;
  target.joints = {};

  // Apply overrides
  if (override.jawOpen !== undefined) target.jawOpen = override.jawOpen;
  if (override.eyeOpen !== undefined) target.eyeOpen = override.eyeOpen;
  if (override.earTwitchL !== undefined) target.earTwitchL = override.earTwitchL;
  if (override.earTwitchR !== undefined) target.earTwitchR = override.earTwitchR;
  if (override.tailWag !== undefined) target.tailWag = override.tailWag;
  if (override.tailCurl !== undefined) target.tailCurl = override.tailCurl;
  if (override.spineAngle !== undefined) target.spineAngle = override.spineAngle;
  if (override.headLift !== undefined) target.headLift = override.headLift;
  if (override.headTurn !== undefined) target.headTurn = override.headTurn;
  if (override.scratchLeg !== undefined) target.scratchLeg = override.scratchLeg;
  if (override.joints) Object.assign(target.joints, override.joints);
}
